import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, ChevronLeft, Send, Eye, Image as ImageIcon, Trash2, Pencil } from "lucide-react";

const FONTS = ["Verdana", "Arial", "Georgia", "Helvetica", "Tahoma", "Times New Roman", "Trebuchet MS", "Courier New"];
const APPT_FIELDS = [
  { key: "booking_number", label: "Booking Number" },
  { key: "price",          label: "Price" },
  { key: "duration",       label: "Duration" },
  { key: "staff",          label: "Staff" },
  { key: "description",    label: "Description" },
  { key: "room",           label: "Room" },
];
const FOOTER_FIELDS_LEFT = [
  { key: "address",              label: "Address" },
  { key: "phone",                label: "Phone" },
  { key: "website",              label: "Website" },
];
const FOOTER_FIELDS_MID = [
  { key: "cancellation_policy",  label: "Cancellation Policy" },
  { key: "calendar_link",        label: "Calendar Link - Google, Apple, Outlook & More" },
  { key: "like_facebook",        label: "Like Us on Facebook" },
];
const FOOTER_FIELDS_RIGHT = [
  { key: "follow_twitter",       label: "Follow Us on Twitter" },
  { key: "online_booking_link",  label: "Online Booking Link" },
];

export default function MarketingEmails() {
  const { bid } = useParams();
  const { staff } = useAuth();
  const canCreate = hasPermission(staff, "location.customer_emails.create");
  const canEdit = hasPermission(staff, "location.customer_emails.edit");
  const canDelete = hasPermission(staff, "location.customer_emails.delete");
  const [branch, setBranch] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited (full-page view)
  const [logoBust, setLogoBust] = useState(0);   // cache-buster after upload/clear
  const [pendingDelete, setPendingDelete] = useState(null);
  const logoInputRef = React.useRef(null);

  const load = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([staffApi.get("/branches"), staffApi.get(`/branches/${bid}/email-templates`)]);
      setBranch(b.data.find((x) => x.id === bid) || null);
      setTemplates(t.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [bid]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (t) => {
    try {
      const r = await staffApi.get(`/email-templates/${t.id}`);
      setEditing(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyTemplate = async (t) => {
    try {
      const r = await staffApi.post(`/email-templates/${t.id}/copy`);
      toast.success(`Copied — "${r.data.name}"`);
      load();
      setEditing(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTemplate = async () => {
    if (!pendingDelete) return;
    try {
      await staffApi.delete(`/email-templates/${pendingDelete.id}`);
      toast.success(`Deleted "${pendingDelete.name}"`);
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openNew = () => {
    setEditing({
      id: null,
      kind: "custom",
      name: "",
      description: "",
      subject: "",
      disabled: false,
      font: "Verdana",
      from_name: `Cocoon Medical Spa${branch?.name ? `, ${branch.name}` : ""}`,
      show_header_logo: true,
      greeting: "Hi",
      greeting_first_name: true,
      greeting_last_name: false,
      body_starts_html: "",
      body_ends_html: "",
      appointment_details: { booking_number: true, price: true, duration: false, staff: false, description: false, room: false },
      valediction: "Enjoy your service,",
      footer: { address: true, phone: true, website: true, cancellation_policy: true, calendar_link: false, like_facebook: true, follow_twitter: true, online_booking_link: false },
      trigger: null,
      enabled: true,
      can_copy: true,
      _new: true,
    });
  };

  // ---- Logo upload / clear ----
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await staffApi.post(`/branches/${bid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded — it will now appear on every enabled email header.");
      setLogoBust(Date.now());
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { if (logoInputRef.current) logoInputRef.current.value = ""; }
  };

  const clearLogo = async () => {
    if (!window.confirm("Remove the branch logo from all customer emails?")) return;
    try {
      await staffApi.delete(`/branches/${bid}/logo`);
      toast.success("Logo removed");
      setLogoBust(Date.now());
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing) {
    return (
      <EmailTemplateEditor
        template={editing}
        bid={bid}
        branch={branch}
        onBack={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  const backendOrigin = process.env.REACT_APP_BACKEND_URL || "";
  const hasLogo = !!branch?.logo_path;
  const logoUrl = hasLogo ? `${backendOrigin}/api/branch-logo/${bid}?v=${logoBust}` : null;

  return (
    <StaffShell context="branch" branchName={branch?.name}>
      <div className="max-w-6xl">
        <p className="tracking-overline text-xs text-muted-foreground">MARKETING</p>
        <div className="flex items-center justify-between mt-1 mb-1 flex-wrap gap-3">
          <h1 className="text-3xl font-semibold" data-testid="emails-title">Customer Emails</h1>
          <div className="flex items-center gap-2 flex-wrap">
            {hasLogo && (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 pl-2 pr-1 py-1" data-testid="logo-preview">
                <img src={logoUrl} alt="Branch logo" className="h-8 max-w-[120px] object-contain" />
                {canEdit && (
                  <button onClick={clearLogo} className="h-7 w-7 rounded hover:bg-rose-50 text-rose-700 flex items-center justify-center" title="Remove logo" data-testid="clear-logo-btn"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            )}
            {canEdit && (
              <>
                <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="logo-file-input" />
                <Button variant="outline" onClick={() => logoInputRef.current?.click()} data-testid="upload-logo-btn" className="gap-1">
                  <ImageIcon className="w-4 h-4" /> {hasLogo ? "Replace logo" : "Upload logo"}
                </Button>
              </>
            )}
            {canCreate && (
              <Button onClick={openNew} data-testid="new-email-btn" className="gap-1"><Plus className="w-4 h-4" /> New email setup</Button>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-6">Automated messages sent throughout the customer journey. Click a row to edit the copy, choose a delivery trigger, or duplicate as a custom notification. The uploaded logo appears at the top of every email whose <span className="italic">Show in header · Logo Image</span> is ticked.</p>

        <Card className="rounded-lg">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/60">
                  <TableHead className="uppercase text-[11px] tracking-wider">Name</TableHead>
                  <TableHead className="uppercase text-[11px] tracking-wider">Description</TableHead>
                  <TableHead className="uppercase text-[11px] tracking-wider">Delivery</TableHead>
                  <TableHead className="uppercase text-[11px] tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">Loading templates…</TableCell></TableRow>
                ) : templates.map((t) => {
                  const isPreset = t.kind && t.kind !== "custom";
                  const showDelete = canDelete && !isPreset;
                  const anyAction = canEdit || (t.can_copy !== false && canCreate) || showDelete;
                  return (
                  <TableRow key={t.id} data-testid={`email-row-${t.kind}`}>
                    <TableCell>
                      {canEdit ? (
                        <button onClick={() => openEdit(t)} className="text-left text-rose-700 hover:text-rose-800 hover:underline font-medium" data-testid={`email-name-${t.kind}`}>{t.name}</button>
                      ) : (
                        <span className="text-left font-medium text-foreground/90" data-testid={`email-name-${t.kind}`}>{t.name}</span>
                      )}
                      {t.disabled && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-700">Disabled</span>}
                    </TableCell>
                    <TableCell className="text-sm text-foreground/80">{t.description || <span className="text-muted-foreground italic">—</span>}</TableCell>
                    <TableCell className="text-xs text-foreground/80" data-testid={`email-trigger-${t.kind}`}><TriggerLabel value={t.trigger} /></TableCell>
                    <TableCell className="text-right text-sm">
                      {!anyAction && <span className="text-muted-foreground italic text-xs">No actions</span>}
                      {canEdit && (
                        <button className="text-rose-700 hover:text-rose-800 hover:underline mr-3 inline-flex items-center gap-1" onClick={() => openEdit(t)} data-testid={`email-edit-${t.kind}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                      )}
                      {canEdit && (
                        <button className="text-rose-700/40 mr-3 cursor-not-allowed" title="Translate — coming soon" aria-disabled="true" disabled data-testid={`email-translate-${t.kind}`}>Translate</button>
                      )}
                      {t.can_copy !== false && canCreate && (
                        <button className="text-rose-700 hover:text-rose-800 hover:underline mr-3" onClick={() => copyTemplate(t)} data-testid={`email-copy-${t.kind}`}>Copy</button>
                      )}
                      {showDelete && (
                        <button
                          className="text-rose-700 hover:text-white hover:bg-rose-600 h-7 w-7 rounded inline-flex items-center justify-center transition-colors"
                          onClick={() => setPendingDelete(t)}
                          title={`Delete "${t.name}"`}
                          data-testid={`email-delete-${t.kind}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPendingDelete(null)} data-testid="delete-email-backdrop">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-email-dialog">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Delete customer email?</h3>
            </div>
            <div className="px-5 py-4 text-sm text-foreground/80 space-y-2">
              <p>You're about to permanently delete <strong>"{pendingDelete.name}"</strong>.</p>
              <p className="text-xs text-muted-foreground">This cannot be undone. Customers scheduled to receive this email will fall back to the default template.</p>
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setPendingDelete(null)} data-testid="delete-email-cancel">Cancel</Button>
              <Button onClick={deleteTemplate} className="bg-rose-600 hover:bg-rose-700 text-white gap-1" data-testid="delete-email-confirm">
                <Trash2 className="w-3.5 h-3.5" /> Delete permanently
              </Button>
            </div>
          </div>
        </div>
      )}
    </StaffShell>
  );
}

function TriggerLabel({ value }) {
  const label = TRIGGER_LABELS[value];
  if (!label) return <span className="text-muted-foreground italic">Not scheduled</span>;
  return <span>{label}</span>;
}

// Kept in lockstep with the backend EMAIL_TRIGGERS list.
const TRIGGER_OPTIONS = [
  { value: "after_booking",                       label: "Right after confirm booking" },
  { value: "after_update",                        label: "Right after booking change (treatment / date / time)" },
  { value: "one_day_before_appt",                 label: "1 day before appointment" },
  { value: "three_days_before_appt",              label: "3 days before appointment" },
  { value: "after_payment",                       label: "Right after receive payment / closed bill" },
  { value: "two_weeks_after_paid",                label: "2 weeks after paid / closed bill" },
  { value: "three_months_after_paid",             label: "3 months after paid / closed bill" },
  { value: "client_birthday",                     label: "Client birthday" },
  { value: "one_month_before_membership_expiry",  label: "1 month before membership expired" },
  { value: "one_month_before_series_expiry",      label: "1 month before series expiry" },
  { value: "one_week_before_series_expiry",       label: "1 week before series expiry" },
  { value: "after_cancel",                        label: "Right after cancel appointment" },
  { value: "after_no_show",                       label: "Right after no show from appointment" },
];
const TRIGGER_LABELS = Object.fromEntries(TRIGGER_OPTIONS.map((t) => [t.value, t.label]));


/* =====================================================================
   EmailTemplateEditor — full-page edit view matching mockup 2.
   ===================================================================== */
function EmailTemplateEditor({ template, bid, branch, onBack, onSaved }) {
  const [form, setForm] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Two-step wizard: step 1 = content (mockup 2), step 2 = delivery trigger.
  // Existing templates open on step 1; a brand-new template still goes step 1
  // first (matching the mockup) but Save is only offered after step 2.
  const [step, setStep] = useState(1);
  const isNew = !template.id || template._new;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleFooter = (k) => set({ footer: { ...form.footer, [k]: !form.footer?.[k] } });
  const toggleAppt = (k) => set({ appointment_details: { ...form.appointment_details, [k]: !form.appointment_details?.[k] } });

  const save = async (openPreview = false) => {
    if (!form.name?.trim()) { toast.error("Template name is required"); return; }
    if (!form.from_name?.trim()) { toast.error("From Name is required"); return; }
    if (!form.subject?.trim()) { toast.error("Subject is required"); return; }
    setSaving(true);
    const payload = {
      kind: form.kind,
      name: form.name.trim(),
      description: (form.description || "").trim(),
      subject: form.subject,
      enabled: !form.disabled,
      disabled: !!form.disabled,
      font: form.font,
      from_name: form.from_name,
      show_header_logo: !!form.show_header_logo,
      greeting: form.greeting,
      greeting_first_name: !!form.greeting_first_name,
      greeting_last_name: !!form.greeting_last_name,
      body_starts_html: form.body_starts_html || "",
      body_ends_html: form.body_ends_html || "",
      body_html: form.body_starts_html || "",
      appointment_details: form.appointment_details,
      valediction: form.valediction,
      footer: form.footer,
      trigger: form.trigger || null,
    };
    try {
      if (form.id) await staffApi.put(`/email-templates/${form.id}`, payload);
      else await staffApi.post(`/branches/${bid}/email-templates`, payload);
      toast.success("Template saved");
      if (openPreview) setPreviewOpen(true);
      else onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <StaffShell context="branch" branchName={branch?.name}>
      <div className="max-w-4xl">
        <button onClick={onBack} className="text-xs text-rose-700 hover:text-rose-800 hover:underline flex items-center gap-1" data-testid="back-to-emails">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Customer Emails
        </button>
        <h1 className="text-3xl font-semibold mt-2 mb-6 border-b border-rose-100 pb-3 text-foreground/90" data-testid="editor-title">{form.name?.trim() ? form.name : (isNew ? "New Email Notification" : "Untitled template")}</h1>

        {/* Two-step stepper header — visible for every template so operators
            know delivery timing is set on step 2. */}
        <div className="flex items-center gap-3 mb-4" data-testid="editor-steps">
          <StepPill n={1} label="Content" active={step === 1} done={step > 1} onClick={() => setStep(1)} />
          <div className="flex-1 h-px bg-border" />
          <StepPill n={2} label="Delivery" active={step === 2} done={false} onClick={() => setStep(2)} />
        </div>

        {step === 1 && (
        <Card className="rounded-lg" data-testid="editor-step-1">
          <CardContent className="p-6 space-y-6">
            {/* Template identity — the two fields the list view shows. */}
            <FieldRow label="Template Name" required>
              <Input
                value={form.name || ""}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Birthday Greeting"
                className="max-w-md"
                data-testid="tmpl-name"
              />
            </FieldRow>
            <FieldRow label="Description">
              <Input
                value={form.description || ""}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="Short summary shown in the Customer Emails list."
                className="max-w-md"
                data-testid="tmpl-description"
              />
            </FieldRow>

            {/* Row: Disabled + Font */}
            <FieldRow label="Email Template">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!form.disabled} onCheckedChange={(v) => set({ disabled: !!v })} data-testid="tmpl-disabled" /> Disabled
              </label>
            </FieldRow>
            <FieldRow label="Font">
              <div className="flex items-center gap-2">
                <Select value={form.font || "Verdana"} onValueChange={(v) => set({ font: v })}>
                  <SelectTrigger className="w-56" data-testid="tmpl-font"><SelectValue /></SelectTrigger>
                  <SelectContent>{FONTS.map((f) => (<SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>))}</SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground" style={{ fontFamily: form.font || "Verdana" }}>{form.font || "Verdana"}</span>
              </div>
            </FieldRow>

            <FieldRow label="From Name" required>
              <Input value={form.from_name || ""} onChange={(e) => set({ from_name: e.target.value })} className="max-w-md" data-testid="tmpl-from-name" />
            </FieldRow>
            <FieldRow label="Subject" required>
              <Input value={form.subject || ""} onChange={(e) => set({ subject: e.target.value })} className="max-w-md" data-testid="tmpl-subject" />
            </FieldRow>

            <FieldRow label="Show in header">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!form.show_header_logo} onCheckedChange={(v) => set({ show_header_logo: !!v })} data-testid="tmpl-show-logo" /> Logo Image
              </label>
            </FieldRow>

            <FieldRow label="Greeting">
              <div className="flex flex-wrap items-center gap-3">
                <Input value={form.greeting || ""} onChange={(e) => set({ greeting: e.target.value })} className="max-w-xs" data-testid="tmpl-greeting" />
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.greeting_first_name} onCheckedChange={(v) => set({ greeting_first_name: !!v })} data-testid="tmpl-greet-first" /> First Name</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!form.greeting_last_name} onCheckedChange={(v) => set({ greeting_last_name: !!v })} data-testid="tmpl-greet-last" /> Last Name</label>
              </div>
            </FieldRow>

            <FieldRow label="Email starts with" required>
              <RichTextArea value={form.body_starts_html || ""} font={form.font} onChange={(v) => set({ body_starts_html: v })} testid="tmpl-body-starts" />
            </FieldRow>

            <FieldRow label="Appointment details">
              <div className="grid grid-cols-3 gap-y-2 gap-x-8 max-w-xl">
                {APPT_FIELDS.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={!!form.appointment_details?.[f.key]} onCheckedChange={() => toggleAppt(f.key)} data-testid={`tmpl-appt-${f.key}`} />
                    {f.label}
                  </label>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="Email ends with">
              <RichTextArea value={form.body_ends_html || ""} font={form.font} onChange={(v) => set({ body_ends_html: v })} testid="tmpl-body-ends" />
            </FieldRow>

            <FieldRow label="Valediction">
              <div>
                <Input value={form.valediction || ""} onChange={(e) => set({ valediction: e.target.value })} className="max-w-md" data-testid="tmpl-valediction" />
                <p className="text-xs text-muted-foreground mt-1">[From Name]</p>
              </div>
            </FieldRow>

            <FieldRow label="Show in footer">
              <div className="grid grid-cols-3 gap-y-2 gap-x-8">
                {[...FOOTER_FIELDS_LEFT, ...FOOTER_FIELDS_MID, ...FOOTER_FIELDS_RIGHT].map((f) => (
                  <label key={f.key} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={!!form.footer?.[f.key]} onCheckedChange={() => toggleFooter(f.key)} data-testid={`tmpl-footer-${f.key}`} />
                    {f.label}
                  </label>
                ))}
              </div>
            </FieldRow>
          </CardContent>
        </Card>
        )}

        {step === 2 && (
        <Card className="rounded-lg" data-testid="editor-step-2">
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-foreground/80">Choose when this email should be sent to the customer. Events fire the moment they happen; time-based rules are checked every 15 minutes and never send twice for the same target.</p>
            <FieldRow label="Delivery trigger" required>
              <Select value={form.trigger || "__none__"} onValueChange={(v) => set({ trigger: v === "__none__" ? null : v })}>
                <SelectTrigger className="max-w-md" data-testid="tmpl-trigger"><SelectValue placeholder="Choose a trigger" /></SelectTrigger>
                <SelectContent className="max-h-[380px]">
                  <SelectItem value="__none__">Not scheduled (draft only)</SelectItem>
                  {TRIGGER_OPTIONS.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </FieldRow>
            <p className="text-xs text-muted-foreground">Each template gets a single trigger — duplicate the template (Copy) if you need the same content on two schedules.</p>
          </CardContent>
        </Card>
        )}

        <div className="flex items-center justify-between gap-2 mt-4 pb-8">
          <div>
            {step === 2 && (
              <Button variant="outline" onClick={() => setStep(1)} data-testid="tmpl-prev-btn"><ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back to content</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onBack} data-testid="tmpl-cancel-btn">Cancel</Button>
            {step === 1 ? (
              <Button onClick={() => setStep(2)} data-testid="tmpl-next-btn" className="gap-1">
                Next: Delivery <Send className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <>
                <Button onClick={() => save(false)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="tmpl-save-btn">
                  <Send className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                </Button>
                <Button onClick={() => save(true)} disabled={saving} variant="secondary" className="gap-1" data-testid="tmpl-save-preview-btn">
                  <Eye className="w-3.5 h-3.5" /> Save and Preview
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {previewOpen && <PreviewDialog form={form} branch={branch} bid={bid} onClose={() => { setPreviewOpen(false); onSaved(); }} />}
    </StaffShell>
  );
}

function StepPill({ n, label, active, done, onClick }) {
  const state = active ? "bg-rose-600 text-white border-rose-600" : done ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-muted text-muted-foreground border-border";
  return (
    <button onClick={onClick} className={`flex items-center gap-2 pl-1 pr-3 h-8 rounded-full border ${state}`} data-testid={`step-pill-${n}`}>
      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${active ? "bg-white text-rose-700" : "bg-white/70"}`}>{n}</span>
      <span className="text-sm">{label}</span>
    </button>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 items-start">
      <div className="pt-2 text-sm">
        {required && <span className="text-rose-600 mr-0.5">*</span>}
        <span className="text-foreground/90">{label}:</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

/* Very small contentEditable-based rich text area to keep the bundle lean.
   It stores raw HTML so the operator can paste from Word/Docs and keep basic
   formatting — plus explicit Bold / Italic / Underline / Unordered-list
   buttons. This intentionally avoids pulling in TipTap or similar just for
   one field. */
function RichTextArea({ value, onChange, font, testid }) {
  const ref = React.useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);
  const cmd = (c, v) => { document.execCommand(c, false, v); ref.current && onChange(ref.current.innerHTML); };
  return (
    <div className="border border-border rounded-md">
      <div className="flex items-center gap-1 border-b border-border bg-muted/40 px-2 py-1 text-xs">
        <button type="button" className="px-2 h-6 rounded hover:bg-background font-bold" onClick={() => cmd("bold")}>B</button>
        <button type="button" className="px-2 h-6 rounded hover:bg-background italic" onClick={() => cmd("italic")}>I</button>
        <button type="button" className="px-2 h-6 rounded hover:bg-background underline" onClick={() => cmd("underline")}>U</button>
        <span className="mx-1 opacity-40">|</span>
        <button type="button" className="px-2 h-6 rounded hover:bg-background" onClick={() => cmd("insertUnorderedList")}>• List</button>
        <button type="button" className="px-2 h-6 rounded hover:bg-background" onClick={() => cmd("insertOrderedList")}>1. List</button>
        <span className="mx-1 opacity-40">|</span>
        <button type="button" className="px-2 h-6 rounded hover:bg-background" onClick={() => cmd("removeFormat")}>Clear</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="min-h-[110px] px-3 py-2 text-sm outline-none prose prose-sm max-w-none"
        style={{ fontFamily: font || "Verdana" }}
        data-testid={testid}
      />
    </div>
  );
}

/* Preview dialog — renders a rough copy of what customers will receive. */
function PreviewDialog({ form, branch, bid, onClose }) {
  const html = useMemo(() => renderPreview(form, branch, bid), [form, branch, bid]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="preview-backdrop">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="preview-dialog">
        <div className="border-b border-border px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">From: {form.from_name || "Cocoon"}</div>
            <div className="font-semibold">{form.subject || "(no subject)"}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
        <div className="p-6" style={{ fontFamily: form.font || "Verdana" }} dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}

function renderPreview(form, branch, bid) {
  const parts = [];
  const backendOrigin = process.env.REACT_APP_BACKEND_URL || "";
  // Header: real branch logo when uploaded, wordmark fallback otherwise.
  if (form.show_header_logo) {
    if (branch?.logo_path && bid) {
      parts.push(`<div style="text-align:center;padding:16px 0"><img src="${backendOrigin}/api/branch-logo/${bid}?v=${Date.now()}" alt="Logo" style="max-height:80px;max-width:260px" /></div>`);
    } else {
      parts.push(`<div style="text-align:center;padding:12px 0"><strong style="letter-spacing:.2em;font-size:22px">COCOON</strong><div style="font-size:12px;color:#888">${escapeHtml(form.from_name || "")}</div></div>`);
    }
  }
  const nameParts = [];
  if (form.greeting_first_name) nameParts.push("Amanda");
  if (form.greeting_last_name) nameParts.push("Nobbs");
  parts.push(`<p>${escapeHtml(form.greeting || "Hi")} ${nameParts.join(" ")},</p>`);
  parts.push(form.body_starts_html || "");
  // Appointment details block — always rendered inside a soft grey rounded card
  // matching the sample email screenshot the user shared.
  const details = form.appointment_details || {};
  const rows = [];
  rows.push(["Date", "31 July 2026<br/>14:00"]);
  if (details.booking_number) rows.push(["Booking #", "100823090953"]);
  if (details.price) rows.push(["Price", "Rp 500.000"]);
  if (details.duration) rows.push(["Duration", "60 minutes"]);
  if (details.staff) rows.push(["Staff", "Nurse Prima"]);
  if (details.description) rows.push(["Description", "A Free Doctor's Consultation"]);
  if (details.room) rows.push(["Room", "Treatment Room 3"]);
  parts.push(`<div style="background:#eeeeee;border-radius:14px;padding:22px 32px;margin:16px auto;display:table" data-testid="preview-details-box"><table style="border-collapse:collapse;margin:auto;text-align:center"><tbody>${rows.map(([k, v]) => `<tr><td colspan='2' style='padding-top:10px'><strong>${escapeHtml(k)}</strong></td></tr><tr><td colspan='2' style='padding-bottom:6px'>${v}</td></tr>`).join("")}</tbody></table></div>`);
  parts.push(form.body_ends_html || "");
  if (form.valediction) parts.push(`<p>${escapeHtml(form.valediction)}<br/><strong>${escapeHtml(form.from_name || "")}</strong></p>`);
  const footerBits = [];
  const f = form.footer || {};
  if (f.address) footerBits.push(branch?.address || "Jalan Sunset Road No. 38, Legian, Bali");
  if (f.phone) footerBits.push(branch?.phone || "+62 811 388 815");
  if (f.website) footerBits.push("www.cocoonmedicalspa.com");
  if (f.cancellation_policy) footerBits.push("Cancellation policy: 24h notice.");
  if (f.calendar_link) footerBits.push("[Add to Google · Apple · Outlook]");
  if (f.online_booking_link) footerBits.push("Book online: cocoonmedicalspa.com/book");
  if (f.like_facebook) footerBits.push("Like us on Facebook");
  if (f.follow_twitter) footerBits.push("Follow us on Twitter");
  if (footerBits.length) parts.push(`<hr style="margin:16px 0;border:none;border-top:1px solid #eee"/><div style="font-size:11px;color:#888;line-height:1.6;text-align:center">${footerBits.map((b) => escapeHtml(b)).join(" · ")}</div>`);
  return parts.join("");
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
